<script setup lang="ts">
definePageMeta({
  layout: 'kurs'
})

const route = useRoute()

// Trailing Slash entfernen, damit Direkt-Aufruf (.../kurs/eltern/) und Prerender-Pfad
// (.../kurs/eltern) denselben Content-Query-Schlüssel verwenden.
const contentPath = computed(() => route.path.replace(/\/+$/, '') || '/')

const { data: page } = await useAsyncData(contentPath.value, () => queryCollection('kurse').path(contentPath.value).first())
if (!page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

// Direkte Kindseiten (Unterseiten dieser Sektion)
const { data: childPages } = await useAsyncData(`${contentPath.value}-children`, () =>
  queryCollection('kurse')
    .where('path', 'LIKE', `${contentPath.value}/%`)
    .select('path', 'title', 'description', 'navigation', 'slug', 'coverImage', 'level')
    .all()
)

const directChildren = computed(() =>
  (childPages.value ?? []).filter((p) => {
    if (p.path === contentPath.value) return false
    const lastSegment = p.path.split('/').at(-1) ?? ''
    if (lastSegment.startsWith('.') || lastSegment === 'index') return false
    const remaining = p.path.startsWith(contentPath.value + '/')
      ? p.path.slice(contentPath.value.length + 1)
      : ''
    return remaining !== '' && !remaining.includes('/')
  })
)

const coursetypeChildren = computed(() => directChildren.value.filter(c => !!c.slug))
const categoryChildren = computed(() => directChildren.value.filter(c => !c.slug))

const courseSlug = computed(() => page.value?.slug)

// Index-Seiten (z.B. 0.index.md) haben keinen Kurs-Slug und sollen keine Zusammenfassung zeigen
const isIndexPage = computed(() => page.value?.stem?.endsWith('index') ?? false)

const { courses, status: coursesStatus } = useLiveCourses(courseSlug, `courses-${courseSlug.value ?? 'none'}`)

const blogFilter = computed(() => page.value?.blogFilter ?? null)

const { formatDate } = useFormatDate()

const { data: filteredPosts } = await useAsyncData(
  `${contentPath.value}-filtered-posts`,
  async () => {
    const filter = blogFilter.value
    if (!filter) return []
    const posts = await queryCollection('posts')
      .where('published', '=', true)
      .order('date', 'DESC')
      .all()
    return posts.filter(p => (p.badge?.label ?? []).includes(filter)).slice(0, 4)
  },
  { watch: [blogFilter] }
)

const DEFAULT_LOCATION = 'Web Professionals GmbH<br>Staudnerbachstrasse 8<br>9472 Grabs'
const DEFAULT_TEACHERS = 'Rolf Eggenberger'

// Default nur einsetzen, wenn der Key komplett fehlt (null/undefined).
// Leerer String → nichts anzeigen.
function withDefault(value: string | null | undefined, fallback: string): string {
  return value === null || value === undefined ? fallback : value
}

const summaryLocation = computed(() => withDefault(page.value?.location, DEFAULT_LOCATION))
const summaryTeachers = computed(() => withDefault(page.value?.teachers, DEFAULT_TEACHERS))
const summaryCost = computed(() => page.value?.cost ?? '')
const summaryDuration = computed(() => page.value?.duration ?? '')
const summaryMaxParticipants = computed(() => page.value?.maxParticipants ?? '')

const nextDates = computed(() => courses.value.slice(0, 3))

const coverImage = computed(() => page.value?.coverImage)

function navIcon(navigation: unknown): string | undefined {
  return typeof navigation === 'object' && navigation !== null && 'icon' in navigation
    ? (navigation as { icon?: string }).icon
    : undefined
}

function formatCompactDate(dateStr: string): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('de-CH', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

const title = page.value.seo?.title || page.value.title
const description = page.value.seo?.description || page.value.description

useSeoMeta({
  title,
  ogTitle: title,
  description,
  ogDescription: description,
  ogImage: coverImage.value,
  ogImageAlt: coverImage.value ? page.value.title : undefined
})

if (!coverImage.value) {
  defineOgImageComponent('Saas')
}

// Course + CourseInstance nur auf echten Kurstyp-Seiten (nicht auf Kategorie-
// Übersichten) – der strukturierte Gegenpart zu den im HTML gerenderten Terminen.
// St. Gallen ist laut FAQ ein wechselnder Mietraum (mal Liip AG, mal Startfeld),
// keine feste Adresse – dafür lieber gar kein `location` behaupten als eine
// falsche Adresse (siehe „W4" in docs/offene-punkte-webpro-llm.md). Nur der
// tatsächliche Standort Grabs ist als Place ausgezeichnet.
const courseLocation = computed(() => /gallen/i.test(page.value?.location ?? '')
  ? undefined
  : { '@type': 'Place', 'name': 'Standort Grabs SG', 'address': { '@type': 'PostalAddress', 'streetAddress': 'Staudnerbachstrasse 8', 'postalCode': '9472', 'addressLocality': 'Grabs', 'addressRegion': 'Sankt Gallen', 'addressCountry': 'CH' } })

useJsonLd(() => (courseSlug.value && page.value)
  ? {
      '@type': 'Course',
      '@id': `${absoluteUrl(contentPath.value)}/#course`,
      'name': page.value.title,
      'description': page.value.description,
      'url': absoluteUrl(contentPath.value),
      'provider': { '@id': ORGANIZATION_ID },
      'hasCourseInstance': courses.value.map(course => ({
        '@type': 'CourseInstance',
        'courseMode': 'Onsite',
        'startDate': course.startdate,
        'endDate': course.enddate || undefined,
        ...(courseLocation.value ? { location: courseLocation.value } : {}),
        ...(course.teachers?.length
          ? {
              instructor: course.teachers
                .filter(t => t.dossier)
                .map(t => ({ '@type': 'Person', 'name': `${t.dossier!.firstname} ${t.dossier!.lastname}`.trim() }))
            }
          : {}),
        ...(course.price_chf
          ? { offers: { '@type': 'Offer', 'price': course.price_chf, 'priceCurrency': 'CHF', 'url': absoluteUrl(contentPath.value) } }
          : {})
      }))
    }
  : null, 'course-schema')
</script>

<template>
  <UPage v-if="page">
    <UPageHeader
      :title="page.title"
      :description="page.description"
    />

    <UPageBody>
      <!-- Zusammenfassung -->
      <UCard
        v-if="!isIndexPage"
        class="mb-8"
      >
        <div class="flex flex-col gap-6 md:flex-row-reverse md:gap-8 md:items-start">
          <NuxtImg
            v-if="coverImage"
            :src="coverImage"
            :alt="page.title"
            class="w-full md:w-2/5 rounded-lg object-contain aspect-video md:aspect-auto dark:bg-white"
            sizes="100vw md:40vw"
          />

          <dl class="md:flex-1 grid gap-x-6 gap-y-3 grid-cols-[max-content_1fr] text-sm">
            <template v-if="summaryLocation">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-map-pin"
                  class="shrink-0"
                />
                Ort
              </dt>
              <dd
                class="text-muted"
                v-html="summaryLocation"
              />
            </template>

            <template v-if="summaryCost">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-tag"
                  class="shrink-0"
                />
                Kosten
              </dt>
              <dd class="text-muted whitespace-pre-line">
                {{ summaryCost }}
              </dd>
            </template>

            <template v-if="summaryTeachers">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-user"
                  class="shrink-0"
                />
                Dozent
              </dt>
              <dd class="text-muted">
                {{ summaryTeachers }}
              </dd>
            </template>

            <template v-if="summaryDuration">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-clock"
                  class="shrink-0"
                />
                Dauer
              </dt>
              <dd class="text-muted">
                {{ summaryDuration }}
              </dd>
            </template>

            <template v-if="summaryMaxParticipants">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-users"
                  class="shrink-0"
                />
                Teilnehmerzahl
              </dt>
              <dd class="text-muted">
                {{ summaryMaxParticipants }}
              </dd>
            </template>

            <template v-if="courseSlug && nextDates.length">
              <dt class="font-semibold text-default flex items-start gap-2">
                <UIcon
                  name="i-lucide-calendar"
                  class="shrink-0"
                />
                Nächste Termine
              </dt>
              <dd>
                <ul class="flex flex-col gap-1 text-muted">
                  <li
                    v-for="course in nextDates"
                    :key="course.id"
                  >
                    {{ formatCompactDate(course.startdate) }}
                  </li>
                </ul>
                <UButton
                  v-if="nextDates[0] && courseSlug !== 'eltern-workshop'"
                  :to="`/anmeldung?kurs=${nextDates[0].id}`"
                  size="sm"
                  trailing-icon="i-lucide-arrow-right"
                  class="mt-3"
                >
                  Anmelden
                </UButton>
              </dd>
            </template>
          </dl>
        </div>
      </UCard>

      <ContentRenderer
        v-if="page.body"
        :value="page"
      />

      <!-- Coursetypes dieser Kategorie -->
      <template v-if="coursetypeChildren.length">
        <USeparator class="my-8" />
        <div class="grid gap-6 md:grid-cols-2 my-6">
          <CoursetypeCard
            v-for="child in coursetypeChildren"
            :key="child.path"
            :title="child.title"
            :description="child.description"
            :slug="child.slug!"
            :to="child.path"
            :icon="navIcon(child.navigation)"
            :cover-image="child.coverImage"
            :level="child.level"
          />
        </div>
      </template>

      <!-- Verwandte Lehrgänge -->
      <template v-if="page.relatedLehrgaenge?.length">
        <USeparator class="my-8" />
        <h2 class="text-xl font-semibold mb-4">
          Passende Lehrgänge
        </h2>
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <NuxtLink
            v-for="lehrgang in page.relatedLehrgaenge"
            :key="lehrgang.to"
            :to="lehrgang.to"
            class="group"
          >
            <UCard class="h-full transition-shadow group-hover:ring-2 group-hover:ring-primary">
              <template #header>
                <div class="flex items-start justify-between gap-2">
                  <div class="flex items-center gap-3 min-w-0">
                    <UIcon
                      v-if="lehrgang.icon"
                      :name="lehrgang.icon"
                      class="text-primary text-xl shrink-0"
                    />
                    <h3 class="font-semibold text-sm leading-tight">
                      {{ lehrgang.title }}
                    </h3>
                  </div>
                  <UBadge
                    color="primary"
                    variant="subtle"
                    class="shrink-0"
                  >
                    Lehrgang
                  </UBadge>
                </div>
              </template>
              <p
                v-if="lehrgang.description"
                class="text-sm text-muted line-clamp-3"
              >
                {{ lehrgang.description }}
              </p>
            </UCard>
          </NuxtLink>
        </div>
      </template>

      <!-- Unterkategorien (ohne Coursetype-Slug) -->
      <template v-if="categoryChildren.length">
        <USeparator class="my-8" />
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 my-6">
          <NuxtLink
            v-for="child in categoryChildren"
            :key="child.path"
            :to="child.path"
            class="group"
          >
            <UCard class="h-full transition-shadow group-hover:ring-2 group-hover:ring-primary">
              <template #header>
                <div class="flex items-center gap-3">
                  <UIcon
                    v-if="navIcon(child.navigation)"
                    :name="navIcon(child.navigation)!"
                    class="text-primary text-xl shrink-0"
                  />
                  <h3 class="text-sm font-semibold leading-tight">
                    {{ child.title }}
                  </h3>
                </div>
              </template>
              <p
                v-if="child.description"
                class="text-sm text-muted line-clamp-3"
              >
                {{ child.description }}
              </p>
            </UCard>
          </NuxtLink>
        </div>
      </template>

      <template v-if="courseSlug">
        <USeparator class="my-8" />

        <div class="my-6">
          <h2 class="text-xl font-semibold mb-4">
            Nächste Kurstermine
          </h2>

          <div
            v-if="coursesStatus === 'pending' || coursesStatus === 'idle'"
            class="flex justify-center py-8"
          >
            <UIcon
              name="i-lucide-loader-circle"
              class="animate-spin text-2xl text-primary"
            />
          </div>

          <div
            v-else-if="courses.length"
            class="grid gap-4 md:grid-cols-2"
          >
            <CourseCard
              v-for="course in courses"
              :key="course.id"
              :course="course"
            />
          </div>

          <WaitinglistForm
            v-else
            :coursetype-slug="courseSlug!"
            :coursetype-title="page?.title"
          />
        </div>
      </template>

      <template v-if="blogFilter && filteredPosts && filteredPosts.length">
        <USeparator class="my-12" />

        <div class="my-6">
          <h2 class="text-xl font-semibold mb-4">
            Blogbeiträge zum Thema "{{ blogFilter }}"
          </h2>

          <UBlogPosts class="lg:grid-cols-2">
            <UBlogPost
              v-for="(post, index) in filteredPosts"
              :key="index"
              :to="post.path"
              :title="post.title"
              :description="post.description"
              :image="post.image"
              :date="post.date"
              :authors="post.authors"
              orientation="vertical"
              variant="naked"
              :ui="{
                description: 'line-clamp-2'
              }"
            >
              <template
                v-if="post.badge?.label?.length"
                #badge
              >
                <div class="flex flex-wrap items-center gap-1">
                  <UBadge
                    v-for="label in post.badge.label.slice(0, 2)"
                    :key="label"
                    :label="label"
                    variant="subtle"
                  />
                </div>
              </template>
              <template #date>
                {{ formatDate(post.date) }}
              </template>
            </UBlogPost>
          </UBlogPosts>

          <div class="flex justify-center mt-6">
            <UButton
              :to="`/blog/thema/${labelToSlug(blogFilter)}`"
              variant="subtle"
              trailing-icon="i-lucide-arrow-right"
            >
              Mehr Beiträge
            </UButton>
          </div>
        </div>
      </template>

      <!--
      <USeparator v-if="surround?.length" />
      <UContentSurround :surround="surround" />
      -->
    </UPageBody>

    <template
      v-if="page?.body?.toc?.links?.length || (courseSlug && courses.length)"
      #right
    >
      <UContentToc
        title="Inhaltsverzeichnis"
        :links="page?.body?.toc?.links ?? []"
      >
        <template
          v-if="courseSlug && courses.length"
          #bottom
        >
          <USeparator
            v-if="page?.body?.toc?.links?.length"
            class="my-4"
          />

          <h4 class="text-sm font-semibold mb-3">
            Nächste Termine
          </h4>
          <ul class="flex flex-col gap-2">
            <li
              v-for="course in courses.slice(0, 5)"
              :key="course.id"
            >
              <NuxtLink
                :to="`/anmeldung?kurs=${course.id}`"
                class="text-sm flex items-center justify-between gap-2 hover:text-primary"
              >
                <span>{{ formatCompactDate(course.startdate) }}</span>
                <UIcon
                  name="i-lucide-arrow-right"
                  class="text-xs shrink-0"
                />
              </NuxtLink>
            </li>
          </ul>
        </template>
      </UContentToc>
    </template>
  </UPage>
</template>
